import React from "react";
import { Button } from "@/components/ui/button";
import { SimpleModal, useGlobalModalStack } from "@wtc/ui";
import { EnhancedGenericCreateForm } from "../../../EnhancedGenericCreateForm";
import type { BaseEntity, SimpleTableConfig } from "../../../UniversalCRMTable/types";
import type { EntityFormProps } from "../../types";

type GlobalModalStack = ReturnType<typeof useGlobalModalStack>;

export const EntityEditDeleteModals = <T extends BaseEntity>({
  entityName,
  config,
  globalModalStack,
  editingItem,
  entityFormProps,
  deleteMutation,
  handleItemUpdated,
  handleItemDeleted,
  handleCancelDelete,
}: {
  entityName: string;
  config: SimpleTableConfig<T>;
  globalModalStack: GlobalModalStack;
  editingItem: T | null;
  entityFormProps?: EntityFormProps<T>;
  deleteMutation: { isPending: boolean };
  handleItemUpdated: (data: Partial<T>) => void | Promise<void>;
  handleItemDeleted: () => void | Promise<void>;
  handleCancelDelete: () => void;
}) => {
  return (
    <>
      {globalModalStack.isModalOpen(`edit-${entityName}`) && (
        <SimpleModal
          opened={globalModalStack.isModalOpen(`edit-${entityName}`)}
          onClose={() => globalModalStack.closeModal(`edit-${entityName}`)}
          title={`Edit ${config.displayName}`}
          size="lg"
        >
          {editingItem && (
            <EnhancedGenericCreateForm<T>
              entityName={entityName}
              config={config}
              initialValues={editingItem}
              onSubmit={handleItemUpdated}
              expandInlineVariant="floating"
              {...(entityFormProps || {})}
            />
          )}
        </SimpleModal>
      )}

      {globalModalStack.isModalOpen(`delete-${entityName}`) && (
        <SimpleModal
          opened={globalModalStack.isModalOpen(`delete-${entityName}`)}
          onClose={() => globalModalStack.closeModal(`delete-${entityName}`)}
          title={`Delete ${config.displayName}`}
          size="sm"
        >
          <div className="space-y-4">
            <p className="text-gray-600 dark:text-gray-400">
              Are you sure you want to delete this {config.displayName.toLowerCase()}? This action
              cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleCancelDelete}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleItemDeleted}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting..." : "Delete"}
              </Button>
            </div>
          </div>
        </SimpleModal>
      )}
    </>
  );
};
